import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { WEB_SOCKET_URL } from 'src/config-global';
import { useRouter } from 'src/routes/hooks';
import {
  onCloseProgressQueue,
  setProgressQueue,
} from 'src/store/progressQueueSlice';
import './loading-dots.css';
import LoadingDots from 'src/components/loading-dots';

export const ProgressQueue = () => {
  // context
  const { user } = useAuthContext();
  const location = useLocation();
  const router = useRouter();

  // ref
  const containerRef = useRef(null);

  // hooks
  const dispatch = useDispatch();
  const progressQueue = useSelector(state => state.progressQueue.progressQueue);

  // variables
  // const { readyState, lastJsonMessage } = useWebSocket(wsUrl, {
  //   shouldReconnect: closeEvent => true,
  //   reconnectAttempts: 10,
  //   reconnectInterval: attemptNumber =>
  //     Math.min(2 ** attemptNumber * 1000, 10000),
  // });

  // states
  const [isMinimized, setIsMinimized] = useState(false);
  const [containerText, setContainerText] = useState('Adding Candidates');
  const [webSocket, setWebSocket] = useState(null);
  const [webSocketStatus, setWebSocketStatus] = useState('DISCONNECTED');

  // variables
  const refetchFunction = useSelector(
    state => state.progressQueue.refetchFunction,
  );

  // functions
  const handleMinimize = () => {
    setIsMinimized(prevProps => !prevProps);
  };

  const wsUrl = useMemo(
    () =>
      WEB_SOCKET_URL && user && `${WEB_SOCKET_URL}?client_id=${user.user_id}`,
    [user],
  );

  const openWebSocket = () => {
    if (webSocketStatus === 'DISCONNECTED' && wsUrl) {
      const newWebSocket = new WebSocket(wsUrl);

      newWebSocket.onopen = event => {
        console.log('opened');
        setWebSocket(newWebSocket);
        setWebSocketStatus('CONNECTED');
      };

      newWebSocket.onclose = event => {
        console.log('closed');
        setWebSocket(null);
        setWebSocketStatus('DISCONNECTED');
      };

      newWebSocket.onmessage = event => {
        const lastJsonMessage = JSON.parse(event.data);

        if (user) {
          const storedProgressQueue = JSON.parse(
            localStorage.getItem(`${user.user_id}_progressQueue`),
          );

          if (storedProgressQueue) {
            const currProgressQueue = storedProgressQueue
              .map(item => {
                if (new Date().getTime() > item.expiration) {
                  return null;
                }

                if (
                  lastJsonMessage !== null &&
                  item.bulkRequestId === lastJsonMessage.message.bulk_request_id
                ) {
                  item.status = lastJsonMessage.message.bulk_request_status;
                  const failedCount = lastJsonMessage.message.failed_count;
                  const errorFileUrl = lastJsonMessage.message.error_file_url;

                  if (failedCount && failedCount > 0) {
                    item.failedCount = failedCount;
                    item.errorFileUrl = errorFileUrl;
                  }
                }

                return item;
              })
              .filter(item => item !== null);

            dispatch(setProgressQueue(currProgressQueue));
            localStorage.setItem(
              `${user.user_id}_progressQueue`,
              JSON.stringify(currProgressQueue),
            );
          }
        }
      };
    }
  };

  const closeWebSocket = () => {
    if (webSocketStatus === 'CONNECTED') {
      webSocket.close();
    }
  };

  const downloadErrorFiles = (errorFileUrl, jobName) => {
    const element = document.createElement('a');
    element.href = errorFileUrl;
    element.download = `error-file-${jobName}.xlsx`;

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const onJobClick = jobId => {
    const url = `/dashboard/jobs/${jobId}/candidates`;
    if (location.pathname === url && refetchFunction) refetchFunction();
    else router.push(`/dashboard/jobs/${jobId}/candidates`);
  };

  // effects
  useEffect(() => {
    if (!containerRef.current) return;

    const contentHeight = containerRef.current.scrollHeight;

    if (isMinimized) {
      containerRef.current.style.height = '50px';
    } else {
      containerRef.current.style.height =
        contentHeight >= 300 ? `300px` : `${contentHeight}px`;
    }
  }, [isMinimized]);

  useEffect(() => {
    if (progressQueue && progressQueue.length) {
      if (progressQueue.find(item => item.status === '')) {
        setContainerText('Adding Candidates');
      } else {
        setContainerText('Candidates Added');
      }
    }
  }, [progressQueue]);

  useEffect(() => {
    if (progressQueue && progressQueue.length) openWebSocket();
    else closeWebSocket();
    return () => {
      closeWebSocket();
    };
  }, [progressQueue]);

  useEffect(() => {
    if (user) {
      const storedProgressQueue = JSON.parse(
        localStorage.getItem(`${user.user_id}_progressQueue`),
      );

      if (storedProgressQueue) {
        const currProgressQueue = storedProgressQueue
          .map(item => {
            if (new Date().getTime() > item.expiration) {
              return null;
            }
            return item;
          })
          .filter(item => item !== null);

        dispatch(setProgressQueue(currProgressQueue));
        localStorage.setItem(
          `${user.user_id}_progressQueue`,
          JSON.stringify(currProgressQueue),
        );
      }
    }
  }, [dispatch, user]);

  return location.pathname.includes('/dashboard/jobs') &&
    progressQueue &&
    progressQueue.length ? (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 30,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        border: theme => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
        boxShadow: theme => theme.customShadows.card,
        width: '300px',
        maxHeight: '300px',
        transition: 'height 0.5s',
        zIndex: 'modal',
        bgcolor: 'white',
      }}
    >
      <Stack>
        <Stack
          direction="row"
          bgcolor="whitesmoke"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1}
          sx={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
        >
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                {containerText}
              </Typography>
              {progressQueue.find(item => item.status === 'FAILED') && (
                <Iconify icon="material-symbols:error" color="warning" />
              )}
              {progressQueue.find(
                item => item.status !== 'FAILED' && item.status !== '',
              ) && <Iconify icon="mdi:tick-circle" color="success" />}
            </Stack>
            {progressQueue.find(item => item.status === '') && <LoadingDots />}
          </Stack>
          <Stack direction="row" alignItems="center">
            <IconButton size="small" onClick={handleMinimize}>
              <Iconify
                sx={{
                  transform: isMinimized ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease-in-out',
                  width: 18,
                  height: 18,
                }}
                icon="ep:arrow-up"
              />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => dispatch(onCloseProgressQueue({ user }))}
            >
              <Iconify
                sx={{ width: 18, height: 18 }}
                icon="material-symbols:close"
              />
            </IconButton>
          </Stack>
        </Stack>
        <Stack overflow="auto" maxHeight={350}>
          {progressQueue.map(item => (
            <Stack
              key={item.bulkRequestId}
              direction="row"
              alignItems="center"
              sx={{
                borderBottom: theme =>
                  `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
              }}
              px={2}
              py={1.5}
              spacing={1}
            >
              {item.status ? (
                <Iconify
                  color={item.status === 'COMPLETED' ? 'success' : 'error'}
                  icon={
                    item.status === 'COMPLETED'
                      ? 'mdi:tick-circle'
                      : 'material-symbols:error'
                  }
                />
              ) : (
                <CircularProgress sx={{ mr: 0.5 }} size={16} />
              )}
              <Stack
                direction="row"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box
                  component="span"
                  onClick={() => onJobClick(item.jobId)}
                  sx={{
                    mx: 0.5,
                    fontSize: 14,
                    color: item.status === '' ? '' : 'primary.main',
                    '&:hover': {
                      textDecoration: item.status !== '' && 'underline',
                      cursor: item.status !== '' && 'pointer',
                    },
                  }}
                >
                  {item.jobName}
                </Box>
                {item.failedCount && (
                  <Tooltip arrow title="Download">
                    <Box
                      component="span"
                      onClick={() =>
                        downloadErrorFiles(item.errorFileUrl, item.jobName)
                      }
                      sx={{
                        mx: 0.5,
                        fontSize: 14,
                        color: 'error.main',
                        '&:hover': {
                          textDecoration: item.status !== '' && 'underline',
                          cursor: item.status !== '' && 'pointer',
                        },
                      }}
                    >
                      Error Files
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  ) : null;
};
